import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ── helpers ──────────────────────────────────────────────────────────────────

const VAT_RATE = 0.12; // 12% MVA
function vatOf(amount) { return (amount || 0) * (VAT_RATE / (1 + VAT_RATE)); } // herav MVA
function exclVat(amount) { return (amount || 0) / (1 + VAT_RATE); }           // eksl. MVA

function fmtNOK(val) {
  return `${Number(val || 0).toLocaleString('no-NO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} NOK`;
}

function fmtDate(d) {
  return new Date(d).toLocaleDateString('no-NO', { day: '2-digit', month: 'short', year: 'numeric' });
}

function getDateRangeBounds(range, customFrom, customTo) {
  const now = new Date();
  switch (range) {
    case 'today': {
      const s = new Date(now); s.setHours(0, 0, 0, 0);
      const e = new Date(now); e.setHours(23, 59, 59, 999);
      return { from: s, to: e, label: `I dag – ${fmtDate(now)}` };
    }
    case 'week': {
      const s = new Date(now); s.setDate(now.getDate() - 6); s.setHours(0, 0, 0, 0);
      const e = new Date(now); e.setHours(23, 59, 59, 999);
      return { from: s, to: e, label: `Siste 7 dager (${fmtDate(s)} – ${fmtDate(e)})` };
    }
    case 'month': {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      const e = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      return { from: s, to: e, label: `Denne måneden (${fmtDate(s)} – ${fmtDate(e)})` };
    }
    case 'lastmonth': {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      return { from: s, to: e, label: `Forrige måned (${fmtDate(s)} – ${fmtDate(e)})` };
    }
    case 'year': {
      const s = new Date(now.getFullYear(), 0, 1);
      const e = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
      return { from: s, to: e, label: `${now.getFullYear()}` };
    }
    case 'custom':
      return {
        from: new Date(customFrom),
        to: new Date(new Date(customTo).setHours(23, 59, 59, 999)),
        label: `${fmtDate(customFrom)} – ${fmtDate(customTo)}`,
      };
    case 'all':
    default:
      return { from: null, to: null, label: 'Alle tider' };
  }
}

// ── main export ──────────────────────────────────────────────────────────────

/**
 * generateReport(orders, refundedItems, refundedOrders, products, options)
 *
 * orders         – array from admin.jsx state
 * refundedItems  – { "ref_itemName": qty }
 * refundedOrders – { "orderRef": true }
 * products       – array from Firestore products collection (for category map)
 * options        – { range, customFrom, customTo }
 */
export function generateReport(orders, refundedItems, refundedOrders, products = [], options = {}) {
  const { range = 'month', customFrom, customTo, password } = options;
  const bounds = getDateRangeBounds(range, customFrom, customTo);

  // ── Build product → category map ────────────────────────────────────────
  const productCategoryMap = {};
  products.forEach(p => {
    productCategoryMap[p.name?.toLowerCase()] = p.category || 'Ukjent';
    productCategoryMap[p.id?.toLowerCase()] = p.category || 'Ukjent';
  });

  const KEYWORD_CATEGORY_MAP = [
    { keywords: ['golf', 'minigolf', 'mini golf'], category: 'golf' },
    { keywords: ['lek', 'hopp', 'trampoline', 'ninja', 'klatring'], category: 'lek' },
    { keywords: ['klippekort', 'klippe', 'stempel'], category: 'klippekort' },
    { keywords: ['månedskort', 'måneds', 'månedlig', 'månedspas'], category: 'månedskort' },
    { keywords: ['gavekort', 'gave'], category: 'gavekort' },
  ];

  function getCategoryForItem(item) {
    // Use item.category directly if present and meaningful
    if (item.category && item.category.toLowerCase() !== 'ukjent') return item.category.toLowerCase().trim();
    const itemName = item.name;
    if (!itemName) return 'Ukjent';
    const key = itemName.toLowerCase().trim();
    // Exact product name lookup (skip Ukjent)
    if (productCategoryMap[key] && productCategoryMap[key].toLowerCase() !== 'ukjent') return productCategoryMap[key].toLowerCase();
    // Fuzzy: check if any product name is contained in the item name (skip Ukjent)
    for (const [pName, cat] of Object.entries(productCategoryMap)) {
      if (cat && cat.toLowerCase() !== 'ukjent' && (key.includes(pName) || pName.includes(key))) return cat.toLowerCase();
    }
    // Keyword detection for known categories
    for (const { keywords, category } of KEYWORD_CATEGORY_MAP) {
      if (keywords.some(kw => key.includes(kw))) return category;
    }
    return itemName; // fall back to item name as its own category
  }

  // ── Filter by date + active status (CAPTURED or refunded only) ────────────
  const filtered = orders.filter(o => {
    // Only include orders that have been captured or refunded (exclude pending/unpaid)
    const isActive = o.captureStatus === 'CAPTURED' || !!(refundedOrders && refundedOrders[o.orderReference]);
    if (!isActive) return false;
    if (!bounds.from) return true;
    const d = new Date(o.datePurchased || o.createdAt?.toDate?.() || 0);
    return d >= bounds.from && d <= bounds.to;
  });

  // ── Per-order status ──────────────────────────────────────────────────────
  function getStatus(order) {
    if (refundedOrders[order.orderReference]) return 'fully-refunded';
    const items = order.items || order.products || [];
    let hasPartial = false;
    items.forEach(item => {
      const qty = refundedItems[`${order.orderReference}_${item.name}`] || 0;
      if (qty > 0 && qty < (item.quantity || 1)) hasPartial = true;
    });
    if (hasPartial) return 'partially-refunded';
    if (order.captureStatus === 'CAPTURED') return 'captured-paid';
    return 'pending';
  }

  function getRefundedAmount(order) {
    const items = order.items || order.products || [];
    return items.reduce((sum, item) => {
      const qty = refundedItems[`${order.orderReference}_${item.name}`] || 0;
      return sum + qty * (item.price || 0);
    }, 0);
  }

  // ── Aggregate totals ──────────────────────────────────────────────────────
  let totalGross = 0, totalRefunds = 0, totalNet = 0;
  let capturedCount = 0, refundedCount = 0, pendingCount = 0;

  // Category map: { catName: { gross, refunds, net, orders, items } }
  const catMap = {};

  filtered.forEach(order => {
    const status = getStatus(order);
    const gross = order.totalPrice || 0;
    const refunded = getRefundedAmount(order);
    const isFullRefund = status === 'fully-refunded';
    const totalRefundedForOrder = isFullRefund ? gross : refunded;
    const net = gross - totalRefundedForOrder;

    totalGross += gross;
    totalRefunds += totalRefundedForOrder;
    totalNet += net;

    if (status === 'captured-paid' || status === 'partially-refunded') capturedCount++;
    else if (status === 'fully-refunded') refundedCount++;
    else pendingCount++;

    // Per-item category breakdown
    const items = order.items || order.products || [];
    if (items.length > 0) {
      items.forEach(item => {
        const cat = getCategoryForItem(item);
        if (!catMap[cat]) catMap[cat] = { gross: 0, refunds: 0, net: 0, orders: new Set(), itemCount: 0 };
        const itemGross = (item.price || 0) * (item.quantity || 1);
        const itemRefunded = isFullRefund
          ? itemGross
          : (refundedItems[`${order.orderReference}_${item.name}`] || 0) * (item.price || 0);
        catMap[cat].gross += itemGross;
        catMap[cat].refunds += itemRefunded;
        catMap[cat].net += itemGross - itemRefunded;
        catMap[cat].orders.add(order.orderReference);
        catMap[cat].itemCount += (item.quantity || 1);
      });
    } else {
      // No item breakdown — put under general
      const cat = 'Generelt';
      if (!catMap[cat]) catMap[cat] = { gross: 0, refunds: 0, net: 0, orders: new Set(), itemCount: 0 };
      catMap[cat].gross += gross;
      catMap[cat].refunds += totalRefundedForOrder;
      catMap[cat].net += net;
      catMap[cat].orders.add(order.orderReference);
    }
  });

  const catRows = Object.entries(catMap)
    .map(([cat, d]) => ({
      cat,
      gross: d.gross,
      refunds: d.refunds,
      net: d.net,
      orders: d.orders.size,
      items: d.itemCount,
      pct: totalGross > 0 ? ((d.gross / totalGross) * 100).toFixed(1) : '0.0',
    }))
    .sort((a, b) => a.cat.localeCompare(b.cat, 'no'));

  // ── Build PDF ─────────────────────────────────────────────────────────────
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    ...(password ? {
      encryption: {
        userPassword: password,
        ownerPassword: password + '_owner',
        userPermissions: ['print'],
      }
    } : {}),
  });
  const W = doc.internal.pageSize.getWidth();
  const navy = [1, 15, 60];
  const yellow = [254, 199, 111];
  const light = [245, 247, 250];
  const red = [239, 68, 68];
  const green = [22, 163, 74];

  // Header bar
  doc.setFillColor(...navy);
  doc.rect(0, 0, W, 32, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Inntektsrapport – Playworld', 14, 13);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 210, 230);
  doc.text('Playworld Sørlandet AS  |  Org.nr: 927 789 086  |  Barstølveien 35, 4636 Kristiansand S', 14, 21);
  doc.setFontSize(7.5);
  doc.text('Rapporten er konfidensiell og kun til intern bruk', 14, 27);
  doc.setTextColor(...yellow);
  doc.setFontSize(10);
  doc.text(`Periode: ${bounds.label}`, W - 14, 13, { align: 'right' });
  doc.setTextColor(200, 210, 230);
  doc.setFontSize(8);
  doc.text(`Generert: ${new Date().toLocaleString('no-NO')}`, W - 14, 21, { align: 'right' });

  let y = 40;

  // ── Summary boxes ─────────────────────────────────────────────────────────
  const boxes = [
    { label: 'Brutto inkl. MVA', value: fmtNOK(totalGross), color: navy },
    { label: 'Netto (eksl. MVA)', value: fmtNOK(exclVat(totalNet)), color: green },
    { label: 'Refundert', value: fmtNOK(totalRefunds), color: red },
  ];

  // VAT-line under boxes
  const vatNet = exclVat(totalNet);
  const vatAmount = vatOf(totalNet);

  const bw = (W - 28 - 6) / 3;
  boxes.forEach((box, i) => {
    const x = 14 + i * (bw + 3);
    doc.setFillColor(...light);
    doc.roundedRect(x, y, bw, 22, 3, 3, 'F');
    doc.setDrawColor(...box.color);
    doc.setLineWidth(0.6);
    doc.roundedRect(x, y, bw, 22, 3, 3, 'S');
    doc.setTextColor(...box.color);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.text(box.label, x + bw / 2, y + 7, { align: 'center' });
    doc.setFontSize(11);
    doc.text(box.value, x + bw / 2, y + 16, { align: 'center' });
  });

  y += 30;

  // ── VAT summary line ─────────────────────────────────────────────────────
  doc.setFontSize(8);
  doc.setTextColor(80, 80, 80);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `Netto eksl. MVA: ${fmtNOK(vatNet)}   |   MVA 12%: ${fmtNOK(vatAmount)}   |   Bestillinger: ${filtered.length}`,
    W / 2, y, { align: 'center' }
  );
  y += 5;

  // ── Order status mini-summary ─────────────────────────────────────────────
  doc.setFontSize(8);
  doc.setTextColor(80, 80, 80);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `Innkrevd: ${capturedCount}   |   Refundert: ${refundedCount}   |   Ventende/kansellert: ${pendingCount}   |   Totalt: ${filtered.length} bestillinger`,
    W / 2, y, { align: 'center' }
  );
  y += 8;

  // ── Category breakdown table ──────────────────────────────────────────────
  doc.setDrawColor(...navy);
  doc.setLineWidth(0.3);
  doc.line(14, y, W - 14, y);
  y += 4;
  doc.setTextColor(...navy);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Inntekt per kategori', 14, y);
  y += 5;

  let totaltRowY = null;
  autoTable(doc, {
    startY: y,
    head: [['Kategori', 'Best.', 'Brutto inkl. MVA', 'Refundert', 'Netto eksl. MVA', '%']],
    body: [
      ...catRows.map(r => [
        r.cat.charAt(0).toUpperCase() + r.cat.slice(1),
        r.orders,
        fmtNOK(r.gross),
        r.refunds > 0 ? `-${fmtNOK(r.refunds)}` : '–',
        fmtNOK(exclVat(r.net)),
        `${r.pct}%`,
      ]),
      [
        'TOTALT',
        filtered.length,
        fmtNOK(totalGross),
        totalRefunds > 0 ? `-${fmtNOK(totalRefunds)}` : '–',
        fmtNOK(exclVat(totalNet)),
        '100%',
      ],
    ],
    theme: 'grid',
    styles: { fontSize: 7.5, cellPadding: 2.5, textColor: [30, 30, 30] },
    headStyles: { fillColor: navy, textColor: 255, fontStyle: 'bold', fontSize: 7 },
    alternateRowStyles: { fillColor: light },
    columnStyles: {
      0: { fontStyle: 'bold' },
      1: { halign: 'center' },
      2: { halign: 'right' },
      3: { halign: 'right' },
      4: { halign: 'right', fontStyle: 'bold' },
      5: { halign: 'center' },
    },
    didParseCell(data) {
      const isTotalt = data.section === 'body' && data.row.index === catRows.length;
      if (isTotalt) {
        data.cell.styles.fillColor = [230, 235, 245];
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.textColor = navy;
        data.cell.styles.lineWidth = { top: 0, bottom: 0, left: 0.2, right: 0.2 };
        data.cell.styles.lineColor = [180, 185, 195];
      }
    },
    didDrawCell(data) {
      if (data.section === 'body' && data.row.index === catRows.length) {
        if (data.column.index === 0) totaltRowY = { top: data.cell.y, bottom: data.cell.y + data.cell.height, left: data.cell.x };
        if (data.column.index === 5) totaltRowY && (totaltRowY.right = data.cell.x + data.cell.width);
      }
    },
    margin: { left: 14, right: 14 },
  });

  // Draw navy outer borders on TOTALT row manually
  if (totaltRowY) {
    doc.setDrawColor(...navy);
    doc.setLineWidth(0.8);
    doc.line(totaltRowY.left, totaltRowY.top, totaltRowY.right, totaltRowY.top);       // top
    doc.line(totaltRowY.left, totaltRowY.bottom, totaltRowY.right, totaltRowY.bottom); // bottom
    doc.line(totaltRowY.left, totaltRowY.top, totaltRowY.left, totaltRowY.bottom);     // left
    doc.line(totaltRowY.right, totaltRowY.top, totaltRowY.right, totaltRowY.bottom);   // right
  }

  y = doc.lastAutoTable.finalY + 10;

  // ── Monthly breakdown (if range = year or all) ────────────────────────────
  if (range === 'year' || range === 'all') {
    const monthMap = {};
    filtered.forEach(order => {
      const d = new Date(order.datePurchased || order.createdAt?.toDate?.() || 0);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!monthMap[key]) monthMap[key] = { gross: 0, refunds: 0, net: 0, count: 0 };
      const gross = order.totalPrice || 0;
      const isFullRefund = refundedOrders[order.orderReference];
      const refundAmt = isFullRefund ? gross : getRefundedAmount(order);
      monthMap[key].gross += gross;
      monthMap[key].refunds += refundAmt;
      monthMap[key].net += gross - refundAmt;
      monthMap[key].count++;
    });

    const monthRows = Object.entries(monthMap).sort((a, b) => a[0].localeCompare(b[0]));

    if (monthRows.length > 0) {
      if (y > 220) { doc.addPage(); y = 20; }

      doc.setDrawColor(...navy);
      doc.line(14, y, W - 14, y);
      y += 4;
      doc.setTextColor(...navy);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Månedlig oversikt', 14, y);
      y += 5;

      autoTable(doc, {
        startY: y,
        head: [['Måned', 'Best.', 'Brutto inkl. MVA', 'Refundert', 'Netto eksl. MVA']],
        body: monthRows.map(([month, d]) => {
          const [yr, mo] = month.split('-');
          const label = new Date(Number(yr), Number(mo) - 1, 1)
            .toLocaleDateString('no-NO', { month: 'long', year: 'numeric' });
          return [
            label.charAt(0).toUpperCase() + label.slice(1),
            d.count,
            fmtNOK(d.gross),
            d.refunds > 0 ? `-${fmtNOK(d.refunds)}` : '–',
            fmtNOK(exclVat(d.net)),
          ];
        }),
        theme: 'grid',
        styles: { fontSize: 7.5, cellPadding: 2.5 },
        headStyles: { fillColor: navy, textColor: 255, fontStyle: 'bold', fontSize: 7 },
        alternateRowStyles: { fillColor: light },
        columnStyles: {
          2: { halign: 'right' },
          3: { halign: 'right' },
          4: { halign: 'right', fontStyle: 'bold' },
        },
        margin: { left: 14, right: 14 },
      });

      y = doc.lastAutoTable.finalY + 10;
    }
  }

  // ── Refund detail section ─────────────────────────────────────────────────
  const refundedOrdersList = filtered.filter(o => {
    const status = getStatus(o);
    return status === 'fully-refunded' || status === 'partially-refunded';
  });

  if (refundedOrdersList.length > 0) {
    if (y > 220) { doc.addPage(); y = 20; }

    doc.setDrawColor(...navy);
    doc.setLineWidth(0.3);
    doc.line(14, y, W - 14, y);
    y += 4;
    doc.setTextColor(...navy);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Refusjoner', 14, y);
    y += 5;

    autoTable(doc, {
      startY: y,
      head: [['Ref.', 'Kunde', 'Dato', 'Brutto', 'Refundert', 'Type']],
      body: refundedOrdersList.map(o => {
        const status = getStatus(o);
        const refAmt = status === 'fully-refunded' ? (o.totalPrice || 0) : getRefundedAmount(o);
        return [
          `#${o.orderReference?.substring(0, 8)}`,
          o.buyerName || o.userName || '—',
          o.datePurchased ? fmtDate(o.datePurchased) : '—',
          fmtNOK(o.totalPrice),
          `-${fmtNOK(refAmt)}`,
          status === 'fully-refunded' ? 'Full refusjon' : 'Delvis refusjon',
        ];
      }),
      foot: [[
        '', '',
        'TOTALT',
        fmtNOK(refundedOrdersList.reduce((s, o) => s + (o.totalPrice || 0), 0)),
        `-${fmtNOK(totalRefunds)}`,
        `${refundedOrdersList.length} ordre`,
      ]],
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2.5, textColor: [30, 30, 30] },
      headStyles: { fillColor: navy, textColor: 255, fontStyle: 'bold', fontSize: 8 },
      footStyles: { fillColor: [230, 235, 245], textColor: navy, fontStyle: 'bold', fontSize: 8 },
      alternateRowStyles: { fillColor: light },
      columnStyles: {
        3: { halign: 'right' },
        4: { halign: 'right', textColor: [200, 30, 30], fontStyle: 'bold' },
        5: { halign: 'center' },
      },
      margin: { left: 14, right: 14 },
    });

    y = doc.lastAutoTable.finalY + 10;
  }

  // ── Sammendrag ────────────────────────────────────────────────────────────
  if (y > 240) { doc.addPage(); y = 20; }

  doc.setDrawColor(...navy);
  doc.setLineWidth(0.3);
  doc.line(14, y, W - 14, y);
  y += 4;
  doc.setTextColor(...navy);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Oppsummering', 14, y);
  y += 7;

  const summaryRows = [
    { label: 'Brutto (inkl. MVA)', value: fmtNOK(totalGross), color: navy },
    { label: 'Refusjoner', value: `- ${fmtNOK(totalRefunds)}`, color: red },
    { label: 'Herav MVA (12%)', value: `- ${fmtNOK(vatOf(totalNet))}`, color: [100, 80, 160] },
    { label: 'Netto (eksl. MVA)', value: fmtNOK(exclVat(totalNet)), color: green, dividerBefore: true, large: true },
  ];

  summaryRows.forEach((row) => {
    if (row.dividerBefore) {
      y += 5;
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.2);
      doc.line(14, y - 2, W - 14, y - 2);
      y += 4;
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(row.label, 14, y);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(row.large ? 12 : 11);
    doc.setTextColor(...row.color);
    doc.text(row.value, W - 14, y, { align: 'right' });
    y += row.large ? 9 : 8;
  });

  y += 4;

  // ── Footer on every page ──────────────────────────────────────────────────
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const pH = doc.internal.pageSize.getHeight();
    doc.setFillColor(...navy);
    doc.rect(0, pH - 18, W, 18, 'F');
    doc.setTextColor(200, 210, 230);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text('Playworld Sørlandet AS  |  Org.nr: 927 789 086  |  Barstølveien 35, 4636 Kristiansand S', 14, pH - 8);
    doc.text(`Side ${i} av ${pageCount}`, W - 14, pH - 8, { align: 'right' });
  }

  // ── Save / return ─────────────────────────────────────────────────────────
  const periodKey = { today: 'idag', week: '7dager', month: 'maned', lastmonth: 'forrige-maned', year: new Date().getFullYear(), all: 'alle', custom: 'egendefinert' }[range] || range;
  const filename = `Inntektsrapport_pws_${periodKey}_${new Date().toISOString().slice(0, 10)}.pdf`;

  if (options.returnBase64) {
    return { base64: doc.output('datauristring').split(',')[1], periodLabel: bounds.label, filename };
  }

  doc.save(filename);
}
